import React, { Fragment } from "react";

import Button from "@common/components/Button/Button";
import { ModalProps } from "@common/components/Modal/Modal";
import SelectDropdown, { Option } from "@common/components/SelectDropdown/SelectDropdown";
import { ToggleSection } from "@common/components/ToggleSection/ToggleSection";
import { forwardingOptions, getIntegrationTypeName, IntegrationType, Jira } from "@common/models/integrations";
import { IntegrationLayoutSettings } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings";
import { getIntegrationTypeLogo } from "@main/pages/Administration/util/getIntegrationTypeLogo";
import { Form } from "main/pages/Administration/components/ModalBody/ModalBody.styles";
import { useJiraCloudIntegrationFormConsumer } from "main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/consumers/useJiraIntegrationFormConsumer";

interface JiraIntegrationModalProps {
  Modal: React.ComponentType<ModalProps>;
  modalProps: ModalProps;
  integration?: Jira;
  selectedAppId: string;
  showToggles: boolean;
  onFinish: () => void;
}

export const JiraIntegrationModal = ({
  Modal,
  modalProps,
  integration,
  selectedAppId,
  showToggles,
  onFinish,
}: JiraIntegrationModalProps) => {
  const SaveButton = () => {
    const finalIsButtonDisabled = isButtonDisabled && !hasMetadataKeyChanges;
    return (
      <Button
        size="small"
        tooltip={{
          position: "top",
          text: tooltipText,
          disabled: !finalIsButtonDisabled || disabled,
        }}
        disabled={finalIsButtonDisabled || loading}
        loadingText={loading && "Saving..."}
        onClick={handleFormSubmit}
      >
        Save changes
      </Button>
    );
  };

  const {
    forwardingOption,
    projectOption,
    projectOptions,
    handleFormChange,
    handleFormSubmit,
    disabled,
    loading,
    twoWaySync,
    fieldMapping,
    isButtonDisabled,
    tooltipText,
    integrationLayout,
    moveIntegrationLayoutEntryDown,
    moveIntegrationLayoutEntryUp,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  } = useJiraCloudIntegrationFormConsumer({ selectedAppId, onFinish, integration });

  const optionDidChange = (option: Option, name: string | undefined) => {
    name && handleFormChange(name, option);
  };

  const switchDidChange = (newValue: boolean, name?: string) => {
    if (!name) return;
    handleFormChange(name, newValue);
  };

  return (
    <Modal
      {...modalProps}
      key={IntegrationType.JIRA}
      buttonElement={<SaveButton />}
      heading={`${getIntegrationTypeName(IntegrationType.JIRA)} integration`}
      iconPath={getIntegrationTypeLogo(IntegrationType.JIRA)}
    >
      <Form
        noValidate
        onSubmit={(event) => event.preventDefault()}
      >
        <SelectDropdown
          name="forwardingOption"
          value={forwardingOption}
          options={forwardingOptions}
          placeholder="Please choose"
          label="Forward tickets"
          onChange={optionDidChange}
          isSearchable={false}
          disabled={disabled || loading}
          hasSubtitles
        />
        <SelectDropdown
          name="projectOption"
          value={projectOption}
          options={projectOptions}
          placeholder="Please choose"
          label="Project"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || loading}
        />
        <IntegrationLayoutSettings
          integrationLayout={integrationLayout}
          moveEntryUp={moveIntegrationLayoutEntryUp}
          moveEntryDown={moveIntegrationLayoutEntryDown}
          toggleEntry={toggleIntegrationLayoutEntry}
          metadataKeys={metadataKeys}
          metadataEnabledState={metadataEnabledState}
          toggleMetadataKey={toggleMetadataKey}
          toggleAllMetadataKeys={toggleAllMetadataKeys}
          setMetadataKeysEnabled={setMetadataKeysEnabled}
          resetMetadataKeys={resetMetadataKeys}
        />

        {showToggles && (
          <Fragment>
            <ToggleSection
              name="twoWaySync"
              title="Jira updates Shake"
              bannerText="Unstable &nbsp;• &nbsp;Deprecated"
              subtitle="Changing Jira ticket’s status category will automatically update its Shake status. All other changes to Jira ticket will be listed in Shake ticket too."
              checked={twoWaySync}
              onChange={switchDidChange}
              disabled={disabled || !projectOption || loading}
            />
            <ToggleSection
              name="fieldMapping"
              title="Field mapping"
              bannerText="Unstable &nbsp;• &nbsp;Deprecated"
              subtitle="Shake ticket elements are individually mapped to their own custom Jira fields."
              checked={fieldMapping}
              onChange={switchDidChange}
              disabled={disabled || !projectOption || loading}
            />
          </Fragment>
        )}
      </Form>
    </Modal>
  );
};
